<template>
  <div class="search">
    <div>
      <h2>CHIME/FRB Collaboration (2023) Catalog</h2>
      <h4>
        {{ num2023CatalogBursts }} bursts from 
        {{ num2023CatalogSources }} CHIME/FRB Collaboration (2023) sources, 
        {{ numPrevPublishedBursts}} bursts from 
        {{ numPrevPublishedSources }} previously published sources.
      </h4>
      <p>
        <b style="color:green">
          This data are provided to you by the CHIME/FRB collaboration. When using these data, please cite 
          <a href="https://ui.adsabs.harvard.edu/abs/2023arXiv230108762T/abstract" target="_blank">
           CHIME/FRB Collaboration (2023).
          </a>
        </b>
      </p>
      <p style="color:green">
        <b>Access CHIME/FRB Collaboration (2023) Catalog Public Data at the</b>
        <a href="https://www.canfar.net/storage/vault/list/AstroDataCitationDOI/CISTI.CANFAR/23.0004/data" target="_blank" rel="noopener noreferrer"> Canadian Astronomy Data Centre</a>
        <b> (external website).</b>
      </p>
      <b-row>
        <b-col md="12" class="my-1">
          <b-button
              target="_blank"
              rel="noopener noreferrer"
              href="/analysis"
              block
              variant="outline-info"
          >Click here for Glossary of terms & FITS file information
          </b-button>
        </b-col>
      </b-row>
      <!-- Download Buttons -->
        <b-row>
          <b-col md="6" class="my-1">
            <b-row>
              <b-col sm="6">
                <p><b>CHIME/FRB Collaboration (2023) Catalog</b> Files download</p>
              </b-col>
              <b-col>
                <a
                  class="download-button btn btn-dark"
                  :href="links.catalog_csv"
                  :download="'repeater_catalog.csv'"
                  role="button"
                >
                  .CSV
                </a>
              </b-col>
              <b-col>
                <a
                  class="download-button btn btn-dark"
                  :href="links.catalog_fits"
                  :download="'repeater_catalog.FITS'"
                  role="button"
                >
                  .FITS
                </a>
              </b-col>
              <b-col>
                <a
                  class="download-button btn btn-dark"
                  :href="links.catalog_json"
                  :download="'repeater_catalog.json'"
                  role="button"
                >
                  .JSON
                </a>
              </b-col>
            </b-row>
          </b-col>
          <b-col md="6" class="my-1">
            <b-row>
              <b-col sm="6">
                <p><b>Previously Published Repeaters Catalog</b> Files download</p>
              </b-col>
              <b-col>
                <a
                  class="download-button btn btn-dark"
                  :href="links.precatalog_csv"
                  :download="'pre-repeater_catalog.csv'"
                  role="button"
                >
                  .CSV
                </a>
              </b-col>
              <b-col>
                <a
                  class="download-button btn btn-dark"
                  :href="links.precatalog_fits"
                  :download="'pre-repeater_catalog.FITS'"
                  role="button"
                >
                  .FITS
                </a>
              </b-col>
              <b-col>
                <a
                  class="download-button btn btn-dark"
                  :href="links.precatalog_json"
                  :download="'pre-repeater_catalog.json'"
                  role="button"
                >
                  .JSON
                </a>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      <!-- Data table -->
      <b-container fluid>
        <b-card no-body>
          <b-tabs pills card fill>
            <b-tab title="CHIME/FRB Collaboration (2023) Catalog Events" active>
              <b-card-text>
                <!-- Main table element -->
                <b-row>
                  <b-col md="12" v-if="items.length != 0">
                    <b-table
                        striped
                        bordered
                        outlined
                        responsive
                        sticky-header
                        :items="items"
                        :fields="fields"
                        :small="smallTable"
                        :dark="false"
                        :current-page="1"
                        :per-page="600"
                        :filter="filter"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        :sort-direction="sortDirection"
                        :head-variant="headVariant"
                        @filtered="onFiltered"
                    >
                      <template v-slot:cell(tns_name)="data">
                        <b-link
                            v-bind:href="'/catalog/' + data.item.tns_name"
                            target="_blank"
                        >
                          {{ data.item.tns_name }}
                        </b-link>
                      </template>
                      <template v-slot:cell(repeater_name)="data">
                        <b-link
                            v-bind:href="'/repeaters/' + data.item.repeater_name"
                            target="_blank"
                        >
                          {{ data.item.repeater_name }}
                        </b-link>
                      </template>
                    </b-table>
                  </b-col>
                  <b-col md="8" v-else>
                    <b-spinner
                        v-for="variant in variants"
                        :variant="variant"
                        :key="variant"
                    ></b-spinner>
                  </b-col>
                </b-row>
              </b-card-text>
            </b-tab>
            <b-tab title="Previously Published Repeaters Catalog Events">
              <b-card-text>
                <!-- Main table element -->
                <b-row>
                  <b-col md="12" v-if="pre_catalog_items.length != 0">
                    <b-table
                        striped
                        bordered
                        outlined
                        responsive
                        sticky-header
                        :items="pre_catalog_items"
                        :fields="fields"
                        :small="smallTable"
                        :dark="false"
                        :current-page="1"
                        :per-page="600"
                        :filter="filter"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        :sort-direction="sortDirection"
                        :head-variant="headVariant"
                        @filtered="onFiltered"
                    >
                      <template v-slot:cell(tns_name)="data">
                        <b-link
                            v-bind:href="'/catalog/' + data.item.tns_name"
                            target="_blank"
                        >
                          {{ data.item.tns_name }}
                        </b-link>
                      </template>
                      <template v-slot:cell(repeater_name)="data">
                        <b-link
                            v-bind:href="'/repeaters/' + data.item.repeater_name"
                            target="_blank"
                        >
                          {{ data.item.repeater_name }}
                        </b-link>
                      </template>
                    </b-table>
                  </b-col>
                  <b-col md="8" v-else>
                    <b-spinner
                        v-for="variant in variants"
                        :variant="variant"
                        :key="variant"
                    ></b-spinner>
                  </b-col>
                </b-row>
              </b-card-text>
            </b-tab>
            <template #tabs-end>
              <b-button
                  class="mx-3 mb-auto"
                  variant="success"
                  v-b-modal.modal-1
                  align="center"
              >Select Columns</b-button
              >
              <b-form-group horizontal>
                <b-input-group>
                  <b-form-input v-model="filter" placeholder="Type to Search TNS name" />
                  <b-input-group-append>
                    <b-btn :disabled="!filter" @click="filter = ''"
                    >Clear</b-btn
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </template>
          </b-tabs>
        </b-card>
      </b-container>
      <!-- Display Table Fields modal -->
      <b-modal
          @ok="updatecolumns"
          id="modal-1"
          scrollable
          centered
          size="lg"
          title="Select Columns"
      >
        <b-row>
          <b-col md="12" class="my-1">
            <b-button
                target="_blank"
                rel="noopener noreferrer"
                href="/analysis"
                block
                variant="outline-info"
            >
              Click here for Glossary of terms
            </b-button>
          </b-col>
        </b-row>
        <b-form-group id="fieldform" label=" " v-slot="{ ariaDescribedby }">
          <b-form-checkbox
              v-for="option in columns"
              v-model="showColumns"
              :key="option.key"
              :value="option.label"
              :aria-describedby="ariaDescribedby"
              name="flavour-3a"
          >
            <p>
              <code>{{ option.key }}</code>
            </p>
          </b-form-checkbox>
        </b-form-group>
      </b-modal>
    </div>
  </div>
</template>

<script>
import {filterMixin} from './mixins/filter_mixin.js'
export default {
  mixins: [filterMixin],
  data() {
    return {
      items: [],
      num2023CatalogBursts: 0,
      num2023CatalogSources: 0,
      pre_catalog_items: [],
      numPrevPublishedBursts: 0,
      numPrevPublishedSources: 0,
      fields: [],
      columns: [],
      showColumns: [],
      smallTable: true,
      dark: false,
      fixed: true,
      currentPage: 1,
      perPage: 5,
      pageOptions1: [5, 10, 15, 50, 100, 150, 200],
      sortBy: null,
      plot: null,
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      headVariant: "dark",
      variants: [
        "primary",
        "secondary",
        "danger",
        "warning",
        "success",
        "info",
        "dark",
      ],
      checked: true,
      links: {
        precatalog_csv: "https://storage.googleapis.com/chimefrb-dev.appspot.com/repeater_catalog/pre_chimefrb2023repeaters.csv",
        precatalog_json: "https://storage.googleapis.com/chimefrb-dev.appspot.com/repeater_catalog/pre_chimefrb2023repeaters.json",
        precatalog_fits: "https://storage.googleapis.com/chimefrb-dev.appspot.com/repeater_catalog/pre_chimefrb2023repeaters.FITS",
        catalog_csv: "https://storage.googleapis.com/chimefrb-dev.appspot.com/repeater_catalog/chimefrb2023repeaters.csv",
        catalog_json: "https://storage.googleapis.com/chimefrb-dev.appspot.com/repeater_catalog/chimefrb2023repeaters.json",
        catalog_fits: "https://storage.cloud.google.com/chimefrb-dev.appspot.com/repeater_catalog/chimefrb2023repeaters.FITS",
      },
    };
  },
  mounted() {
    var url = 'https://catalog.chime-frb.ca/repeater-catalog'
    var pre_catalog_url = "https://catalog.chime-frb.ca/pre-repeater-catalog";
    this.$http
      .post(
        url,
        {
          responseType: "json",
          timeout: 10000,
        }
      )
      .then((response) => {
        this.parseResponse(response.body);
      })
      .then(
        this.$http
          .post(
            pre_catalog_url,
            {
              responseType: "json",
              timeout: 10000,
            }
          )
          .then((response) => {
            this.parsePreCatalog(response.body);
          })
      );
  },
  methods: {
    parseResponse(events) {
      this.items = events;
      var columns = [
        'tns_name',
        'repeater_name',
        'ra_1',
        'ra_1_err_low',
        'ra_1_err_up',
        'ra_2',
        'ra_2_err_low',
        'ra_2_err_up',
        'ra_notes',
        'dec_1',
        'dec_1_err_low',
        'dec_1_err_up',
        'dec_2',
        'dec_2_err_low',
        'dec_2_err_up',
        'dec_notes',
        'gl',
        'gb',
        'exp_up',
        'exp_up_err',
        'exp_up_notes',
        'exp_low',
        'exp_low_err',
        'exp_low_notes',
        'bonsai_snr',
        'bonsai_dm',
        'low_ft_90',
        'up_ft_90',
        'low_ft_95',
        'up_ft_95',
        'snr_fitb',
        'dm_fitb',
        'dm_fitb_err',
        'dm_exc_1_ne2001',
        'dm_exc_2_ne2001',
        'dm_exc_1_ymw16',
        'dm_exc_2_ymw16',
        'bc_width',
        'scat_time',
        'scat_time_err',
        'flux',
        'flux_err',
        'flux_notes',
        'fluence',
        'fluence_err',
        'fluence_notes',
        'sub_num',
        'mjd_400',
        'mjd_400_err',
        'mjd_inf',
        'mjd_inf_err',
        'width_fitb',
        'width_fitb_err',
        'sp_idx',
        'sp_idx_err',
        'sp_run',
        'sp_run_err',
        'high_freq',
        'low_freq',
        'peak_freq',
        'chi_sq',
        'dof',
        'flag_frac',
        'R_cc'
      ];
      for (var i = 0; i < columns.length; i++) {
        this.columns.push({
          key: columns[i],
          label: columns[i],
          sortable: true,
          class: "text-center",
        });
      }
      // default columns to be shown
      var default_cols = [
        "tns_name",
        "repeater_name",
        "ra_1",
        "ra_2",
        "dec_1",
        "dec_2",
        "snr_fitb",
        "dm_fitb",
        "bc_width",
        "fluence",
        "fluence_err",
      ];
      this.showColumns = default_cols;
      this.fields = [];
      this.fields.push({
        key: "tns_name",
        label: "tns_name",
        sortable: true,
        class: "text-center",
      });
      for (var i1 = 0; i1 < default_cols.length; i1++) {
        this.fields.push({
          key: default_cols[i1],
          label: default_cols[i1],
          sortable: true,
          class: "text-center",
        });
      }
      this.num2023CatalogBursts = events.length;
      this.num2023CatalogSources = events.map(({ repeater_name }) => (repeater_name)).filter((value, index, array) => array.indexOf(value) === index).length;
    },
    parsePreCatalog(events) {
      this.pre_catalog_items = events;
      this.numPrevPublishedBursts = events.length;
      this.numPrevPublishedSources = events.map(({ repeater_name }) => (repeater_name)).filter((value, index, array) => array.indexOf(value) === index).length;
    },
    updatecolumns() {
      const order = [
        "previous_name",
        "repeater_name",
        "ra_1",
        "ra_1_err_low",
        "ra_1_err_up",
        "ra_2",
        "ra_2_err_low",
        "ra_2_err_up",
        "dec_1",
        "dec_1_err_low",
        "dec_1_err_up",
        "dec_2",
        "dec_2_err_low",
        "dec_2_err_up",
        "bonsai_dm",
        "dm_exc_1_ymw16",
        "dm_exc_1_ne2001",
        "dm_exc_2_ymw16",
        "dm_exc_2_ne2001",
        "bonsai_snr",
        "bc_width",
        "scat_time",
        "scat_time_err",
        "mjd_400",
        "mjd_400_err",
        "mjd_inf",
        "mjd_inf_err",
        "flux",
        "flux_err",
        "fluence",
        "fluence_err",
        "peak_freq",
        "low_freq",
        "high_freq",
        "sp_idx",
        "sp_idx_err",
        "sp_run",
        "sp_run_err",
        "dm_fitb",
        "dm_fitb_err",
        "snr_fitb",
        "width_fitb",
        "width_fitb_err",
        "chi_sq",
        "dof",
        "flag_frac",
        "low_ft_90",
        "up_ft_90",
        "low_ft_95",
        "up_ft_95",
        "exp_low",
        "exp_low_err",
        "exp_up",
        "exp_up_err",
        "sub_num",
        "gb",
        "gl",
      ];
      let choose = this.showColumns;
      let position_of = {};
      for (let i = 0; i < choose.length; i++) {
        for (let j = 0; j < order.length; j++) {
          if (order[j] == choose[i]) {
            position_of[j] = order[j];
          }
        }
      }
      const cols = Object.values(
          Object.fromEntries(Object.entries(position_of).sort())
      );
      this.fields = [];
      this.fields.push({
        key: "tns_name",
        label: "tns_name",
        sortable: true,
        class: "text-center",
      });
      for (var i1 = 0; i1 < cols.length; i1++) {
        this.fields.push({
          key: cols[i1],
          label: cols[i1],
          sortable: true,
          class: "text-center",
        });
      }
      cols.shift();
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },
};
</script>

<style>
.supsub {
  position: absolute;
}
.subscript {
  display: block;
  position: relative;
  left: 2px;
  bottom: -18px;
}
.superscript {
  display: block;
  position: relative;
  left: 2px;
  top: 2px;
}
.download-button {
  display: flex;
  justify-content: center;
  padding: 5px;
  color: #fff;
  opacity: 0.87;
  text-align: center;
}
</style>
